import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Drawer,
  Collapse,
  Typography,
  Skeleton,
} from "@mui/material";
import {
  Close as CloseIcon,
} from "@mui/icons-material";
// Standardizing on Iconsax across the app (confirmed via respond.io's own
// production bundle, see LeftNav.jsx) — this replaces the one leftover MUI
// icon in the Inbox panel heading, and now the first-section filter list icons too.
import {
  SidebarLeft as SidebarToggleIconIx,
  DirectInbox,
  ProfileCircle,
  Activity,
  ProfileRemove,
  TickCircle,
  Forbidden,
  Profile2User,
  People,
  ArrowDown2,
  ArrowUp2,
  Add,
  Magicpen,
} from "iconsax-react";
import { IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useSelector, useDispatch } from "react-redux";
import {
  selectShowInboxSettings,
  selectAccount,
  selectUserDetailsData,
  selectUserPermissions,
  selectInboxRecentCounts,
  selectTeamMembersFromInbox,
  selectTeamsFromInbox,
  selectTeamMembersWithNames,
  selectTeamsWithNames,
  selectActiveMembers,
  selectActiveMembersLoaded,
  selectSelectedInboxTab,
  selectTeamPermissions,
  selectTeamMemberPermissions,
  selectInboxRecentInitialized,
  selectAiAgents,
  selectAiAgentCounts,
  selectAgentByAgentId,
} from "../../redux/selectors";
import InboxNumberFilter from "./InboxNumberFilter";
import { setSelectedOption } from "../../redux/Slice/Settings/settingsSlice";
import { toggleInboxSettings } from "../../redux/Slice/Inbox/inboxSlice";
import {
  fetchInboxTeamData,
} from "../../redux/Slice/Inbox/inboxSettingsSlice";
import { fetchClosedTabInbox, fetchLifecycleTabInbox, fetchAgentTabInbox } from "../../redux/Slice/Inbox/inboxRecentSlice";
import { fetchAiAgents } from "../../redux/Slice/AiAgents/aiAgentsSlice";
import { formatBadgeCount } from "../../utils/formatBadgeCount";
import { fetchUserDetailsCached as fetchUserDetails, fetchUserPermissionsCached as fetchUserPermissions } from "../../redux/Slice/User/userDetailsSlice";
import { fetchAccountDetails } from "../../redux/Slice/Dashboard/dashboardSlice";
import { fetchActiveMembers } from "../../redux/Slice/Inbox/inboxAllSlice";
import { getConnectionStatus, onConnectionChange, onDisconnectionChange } from "../../utils/socketHandler";
import connectionMonitor from "../../utils/connectionMonitor";

import { setSelectedSettings } from "../../redux/Slice/Inbox/inboxSlice";
import { setSelectedTeamMember } from "../../redux/Slice/Inbox/inboxSlice";
import { setSelectedTeam } from "../../redux/Slice/Inbox/inboxSlice";
import { setSelectedInboxTab } from "../../redux/Slice/Inbox/inboxSlice";
import { useNavigate, useParams } from "react-router-dom";
import {
  dispatchInboxSidebarTabChangeClear,
  navigateOffCustomerChatRoute,
} from "../../utils/inboxTabSwitch";
import { fetchLifecycleStagesWithCounts } from "../../redux/Slice/Lifecycle/lifecycleStageSlice";
import { slimScrollbarSx } from "../../utils/scrollbarStyles";
import IconButtonWithTooltip from "./Shared/IconButtonWithTooltip";
import SidebarNavList, { getCountBadgeSx } from "../Navbar/SidebarNavList";
import { shouldShowGroupsTab } from "./groupsTabVisibility";
import { sortByName } from "../../utils/sortByName";

// Shared unread/count badge style — this file has 4 identical count badges
// (filter list, Lifecycle stages, Team Members list, Teams list). Extracted
// so a future fix only needs to happen once instead of drifting out of sync
// (found 2026-08-18: a fix only ever landed on 1 of the 4 copies at a time,
// twice in a row). `width`/`height` fixed equal (not minWidth) forces a true
// circle ALWAYS, even for "99+" — Rahim's explicit call: a perfect circle
// with "99+" touching the edge beats a pill shape. No horizontal padding for
// the same reason (padding only ever fights the fixed width).
// Pill, not a fixed circle (Rahim, 2026-08-29: tried a true circle with a
// shrunk font first, but "9999+" still clipped in MessageCard.jsx's smaller
// row badge even at the smallest legible size — "make pill thats uch
// better"). minWidth, not width — a 1-2 digit count still renders as a
// perfect circle (width naturally equals minWidth); a capped label widens
// into a pill instead of clipping.
// Mirrors the real row's exact box model (ListItemButton padding "8px 10px" + mx:1,
// ListItemIcon minWidth:36 holding a 24x24 icon, getCountBadgeSx's 24px-min pill
// badge) so nothing visually shifts once real content swaps in. Replaces the old
// pre-redesign skeleton shape (thin rectangle "badge", no mx/padding match, 20px
// icon instead of 24px) found still in use 2026-08-18.
const SidebarRowSkeleton = () => (
  <Box sx={{ display: "flex", alignItems: "center", padding: "8px 10px", mx: 1 }}>
    <Box sx={{ minWidth: 36, display: "flex", flexShrink: 0 }}>
      <Skeleton variant="circular" width={24} height={24} />
    </Box>
    <Skeleton variant="text" sx={{ flex: 1, height: 18, mr: 1 }} />
    <Skeleton variant="circular" width={24} height={24} sx={{ flexShrink: 0 }} />
  </Box>
);

// Mirrors the real "Team Member"/"Teams" section header row (ListItemButton
// padding "8px 10px", no mx, 0.9375rem text, 18x18 chevron).
// eslint-disable-next-line no-unused-vars
const SidebarHeaderSkeleton = ({ width = "45%" }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px" }}>
    <Skeleton variant="text" sx={{ width, height: 18 }} />
    <Skeleton variant="circular" width={18} height={18} />
  </Box>
);

// AI Agents/Lifecycle/Team Member/Teams sections: closed by default the first time a
// user ever opens the Inbox, but once they toggle a section, that choice should stick
// across reloads/logins — so the initial state comes from localStorage when present,
// falling back to closed (false) only the first time (no stored value yet). Single
// JSON blob (not 4 separate keys) so a future 5th collapsible section is a one-line add.
const SIDEBAR_SECTIONS_STORAGE_KEY = "inboxSidebarSectionsOpen";

const getStoredSectionOpen = (key) => {
  try {
    const stored = JSON.parse(localStorage.getItem(SIDEBAR_SECTIONS_STORAGE_KEY) || "{}");
    return stored[key] === true;
  } catch {
    // Private browsing / corrupted value — just fall back to the closed default.
    return false;
  }
};

const persistSectionOpen = (key, isOpen) => {
  try {
    const stored = JSON.parse(localStorage.getItem(SIDEBAR_SECTIONS_STORAGE_KEY) || "{}");
    stored[key] = isOpen;
    localStorage.setItem(SIDEBAR_SECTIONS_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // localStorage unavailable (private mode, quota exceeded) — preference just won't
    // survive a reload; the section itself still works fine for this session.
  }
};

const InboxSettings = ({ isInsideDrawer = false, onClose }) => {
  const theme = useTheme();
  // Dark mode: flat white for the Team Member/Teams rows' icon+text, full stop — not just
  // the "selected or active" state (Rahim, 2026-08-27, correcting the first pass: "in dark
  // mode we are using white even tho its not selected" — that's the intended rule, matches
  // ChatInfoRail.jsx's own "use white for all icons" convention for dark mode). The grey-vs-
  // full-color distinction only exists in light mode; dark mode relies on the green online-
  // dot alone to signal active/inactive, not a separate icon/text color.
  const activeRowColor = theme.palette.mode === "dark" ? "#FFFFFF" : theme.palette.text.primary;
  const inactiveRowColor = theme.palette.mode === "dark" ? "#FFFFFF" : theme.palette.text.secondary;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accountid, userid } = useParams();
  // Deep-links the empty Team Member / Teams sections straight to the matching
  // Settings tab (same setSelectedOption+navigate pattern already used by
  // Dashboard's TeamMembersPanel.jsx) — stops the ListItemButton's own onClick
  // (the section's collapse toggle) from also firing.
  const goToSettingsOption = (optionName) => (e) => {
    e.stopPropagation();
    dispatch(setSelectedOption(optionName));
    navigate(`/home/${accountid}/settings`);
  };
  // Same deep-link idea for the empty AI Agents section, but AI Settings is its own
  // top-level route (App.js's "ai" route, defaulting to its Agents tab) rather than a
  // tab inside Settings, so there's no setSelectedOption to dispatch here.
  const goToAiSettings = (e) => {
    e.stopPropagation();
    navigate(`/home/${accountid}/ai`);
  };
  const isXlScreen = useMediaQuery(theme.breakpoints.up("xl"));
  const screenSize = useSelector((state) => state.inbox.screenSize);

  const showInboxSettings = useSelector(selectShowInboxSettings);
  const account = useSelector(selectAccount);
  const currentWorkspace = useSelector((state) => state.workspace?.currentWorkspace);
  const userDetails = useSelector(selectUserDetailsData);
  const userPermissions = useSelector(selectUserPermissions);
  const inboxCounts = useSelector(selectInboxRecentCounts);
  // Badge: number of team-member threads where the logged-in user has unread messages
  const teamMemberChats = useSelector((state) => state.teamMemberChat?.chats || []);
  const teamMemberChatsUnreadThreadCount = useMemo(
    () => teamMemberChats.filter((c) => (c.unreadCount || 0) > 0).length,
    [teamMemberChats]
  );
  
  // Get team members and teams from the correct sources
  const teamMembersFromInbox = useSelector(selectTeamMembersFromInbox);
  const teamsFromInbox = useSelector(selectTeamsFromInbox);
  const teamMembersWithNames = useSelector(selectTeamMembersWithNames);
  const teamsWithNames = useSelector(selectTeamsWithNames);
  const activeMembers = useSelector(selectActiveMembers);
  const activeMembersLoaded = useSelector(selectActiveMembersLoaded);
  // The logged-in user's own presence dot below uses this (tab-local, same
  // source as TopNav.jsx's ring) instead of `activeMembers` — that array only
  // updates when this tab *receives* a server-broadcast user_connected_update/
  // user_disconnected_update event, which can't happen while this tab's own
  // socket is down (it can't receive anything) or while the whole socket-server
  // process is unreachable, leaving your own dot stuck on its last known color.
  // Every other member's dot is unaffected — their status has no other source.
  const [isSocketConnected, setIsSocketConnected] = useState(() => getConnectionStatus().isConnected);
  useEffect(() => {
    const handleConnected = () => setIsSocketConnected(true);
    const handleDisconnected = () => setIsSocketConnected(false);
    onConnectionChange(handleConnected);
    onDisconnectionChange(handleDisconnected);
    setIsSocketConnected(getConnectionStatus().isConnected);
    return () => {
      connectionMonitor.off('connection', handleConnected);
      connectionMonitor.off('disconnection', handleDisconnected);
    };
  }, []);
  const selectedInboxTab = useSelector(selectSelectedInboxTab);
  const TeamPermissions = useSelector(selectTeamPermissions);
  const TeamMemberPermissions = useSelector(selectTeamMemberPermissions);
  const lazyTabsLoaded = useSelector(state => state.inboxRecent?.lazyTabsLoaded || {});
  const teamDataLoading = useSelector(state => state.inboxSettings?.teamDataLoading ?? true);
  // Count of what fetchInboxTeamData itself has actually loaded — NOT teamMembersFromInbox/
  // teamsFromInbox above (those come from inboxRecentSlice's fetchInbox() call, a different
  // fetch that's already populated by the time this mounts). Used only to guard the
  // fetchInboxTeamData() dispatch below against refiring on every presence change.
  const inboxSettingsTeamMembersCount = useSelector(state => state.inboxSettings?.teamMembers?.length ?? 0);
  const inboxSettingsTeamsCount = useSelector(state => state.inboxSettings?.teams?.length ?? 0);
  const inboxRecentInitialized = useSelector(selectInboxRecentInitialized);

  // Lifecycle section (display-only for now — no click/filter behavior yet, that's
  // a separate follow-up pass). Reuses the already-built lifecycleStageSlice
  // (Module 7 of the Lifecycle CRM build) — same stages/counts source as
  // LifecycleSettings.jsx and the Dashboard's LifecyclePipeline, and already kept
  // live via the existing lifecycle_stages_updated/contact_lifecycle_updated
  // socket handlers in socketHandler.js, so no new socket wiring needed here.
  const lifecycleStages = useSelector((state) => state.lifecycleStage.stages);
  const lifecycleCounts = useSelector((state) => state.lifecycleStage.counts);
  const lifecycleLoading = useSelector((state) => state.lifecycleStage.loading);
  // Owner bypasses at the backend (roleMiddleware.js) — the stored Lifecycle.View
  // boolean defaults to false even for Owner, so check roleName too. Same pattern
  // already used in LifecycleStageSelect.jsx/LifecycleSettings.jsx.
  const roleName = useSelector((state) => state.userDetails?.userDetails?.roleName || null);
  const LifecyclePermissions = useSelector(
    (state) => state.userDetails?.userPermissions?.permissions?.Lifecycle || {}
  );
  const canViewLifecycle = roleName === "Owner" || LifecyclePermissions.View;

  // WhatsApp (QR) groups: the Groups tab and the group chats in All need the role's Groups.View (Owner always passes --
  // same "stored flag may be missing for the Owner" reasoning as Lifecycle above). The server enforces it too; this only
  // hides what the user is not allowed to open.
  const groupsPermissions = useSelector(
    (state) => state.userDetails?.userPermissions?.permissions?.Groups || {}
  );
  const permissionsLoaded = useSelector((state) => Boolean(state.userDetails?.userPermissions?.permissions));
  const canViewGroups = roleName === "Owner" || groupsPermissions.View === true;
  // ...and groups exist only on QR numbers: the tab is also tied to the CURRENT workspace having a QR number (see
  // groupsTabVisibility.js). the loaded-workspace check keeps a refresh on the Groups tab from being bounced before the workspace arrives.
  const currentWorkspaceForGroups = useSelector((state) => state.workspace?.currentWorkspace || null);
  const showGroupsTab = shouldShowGroupsTab({ canViewGroups, workspace: currentWorkspaceForGroups });

  // AI Agents section — same "own rail row, own on-demand contact bucket" shape as
  // Lifecycle above, but the roster comes from aiAgentsSlice (workspace-scoped AI
  // agents, see AI Takeover / aiTakeoverEventHandler.js) instead of lifecycleStageSlice.
  // The count badge itself reads aiAgentCounts (fetched upfront with the roster, kept
  // live via adjustAgentCount — see aiAgentsSlice.js), same "survives a refresh"
  // property lifecycleStage.counts has; agentByAgentId is session-only (populated by
  // clicking the row) and only used as a fresher override once that's happened.
  const aiAgents = useSelector(selectAiAgents);
  const aiAgentsLoading = useSelector((state) => state.aiAgents?.loading ?? false);
  const aiAgentCounts = useSelector(selectAiAgentCounts);
  const agentByAgentId = useSelector(selectAgentByAgentId);
  // Show skeleton until userDetails has arrived AND the inbox slice has settled at least
  // once (fetchInbox fulfilled/rejected — see inboxRecentSlice.js). This flag lives on the
  // shared inboxRecent state, so it flips even if THIS mount's own fetchInbox() dispatch
  // loses fetchInbox's global 5s debounce to some other trigger (socket reconnect, another
  // tab's effect, etc.) — whichever call actually wins the race still unblocks every reader.
  // A per-call promise chain (the previous approach here) doesn't have that property: if
  // its own dispatch gets silently debounced, there's no retry and the skeleton never clears.
  const isCoreLoading = !userDetails || !inboxRecentInitialized;

  // Robust name resolution: normalize possible name fields and use maps
  const normalizeId = (id) => (id === undefined || id === null) ? null : String(id);
  const pickName = (obj) => {
    if (!obj) return null;
    return (
      obj.name || obj.Name || obj.teamName || obj.TeamName || obj.displayName || obj.fullName || (obj.firstName && obj.lastName && `${obj.firstName} ${obj.lastName}`) || null
    );
  };

  // Build maps keyed by stringified id/_id for fast lookup
  const teamMemberNameMap = {};
  (teamMembersWithNames || []).forEach((m) => {
    const id = normalizeId(m.id || m._id);
    const name = pickName(m) || null;
    if (id) teamMemberNameMap[id] = name || teamMemberNameMap[id] || null;
  });

  const teamNameMap = {};
  (teamsWithNames || []).forEach((t) => {
    const id = normalizeId(t.id || t._id);
    const name = pickName(t) || null;
    if (id) teamNameMap[id] = name || teamNameMap[id] || null;
  });

  // Helper to resolve name for inbox entries, fallback to other fields
  const resolveMemberName = (member) => {
    if (!member) return null;
    const id1 = normalizeId(member._id);
    const id2 = normalizeId(member.id);
    return (
      teamMemberNameMap[id1] || teamMemberNameMap[id2] || pickName(member) || id1 || id2 || "Unknown"
    );
  };

  const resolveTeamName = (team) => {
    if (!team) return null;
    const id1 = normalizeId(team._id);
    const id2 = normalizeId(team.id);
    return (
      teamNameMap[id1] || teamNameMap[id2] || pickName(team) || id1 || id2 || "Unknown"
    );
  };

  const teamMembers = sortByName(
    (teamMembersFromInbox || []).map((member) => ({
      ...member,
      name: resolveMemberName(member),
    }))
  );

  const teams = sortByName(
    (teamsFromInbox || []).map((team) => ({
      ...team,
      name: resolveTeamName(team),
    }))
  );

  const [isTeamMemberOpen, setIsTeamMemberOpen] = useState(() => getStoredSectionOpen("teamMember"));
  const [isTeamsOpen, setIsTeamsOpen] = useState(() => getStoredSectionOpen("teams"));
  const [isLifecycleOpen, setIsLifecycleOpen] = useState(() => getStoredSectionOpen("lifecycle"));
  const [isAiAgentsOpen, setIsAiAgentsOpen] = useState(() => getStoredSectionOpen("aiAgents"));

  useEffect(() => {
    if (!userDetails) {
      dispatch(fetchUserDetails());
    }
    if (!account) {
      dispatch(fetchAccountDetails());
    }
    // Only fetch permissions if we don't have them
    if (!userPermissions) {
      dispatch(fetchUserPermissions());
    }

    if (userDetails && userPermissions) {
      // Use consolidated API call to fetch both teams and team members data.
      // Guarded the same way as the lifecycle fetch below — without the
      // length check, this re-fires on every presence blip (activeMembers?.length
      // is a dep below, and it changes every time ANY teammate connects/disconnects
      // anywhere in the workspace), each time flipping teamDataLoading to true and
      // collapsing the Team Members/Teams lists down to their loading skeletons —
      // which, since this whole panel is one scroll container, yanks the scrollbar
      // to the top mid-scroll.
      if ((TeamPermissions.View || TeamMemberPermissions.View) && !inboxSettingsTeamMembersCount && !inboxSettingsTeamsCount) {
        dispatch(fetchInboxTeamData());
      }

      // Same length-guard idea as the fetchActiveMembers effect below — without it,
      // this fires again every time ANY of the 8 deps below changes (e.g. activeMembers
      // itself resolving a moment after this effect's first run), relying entirely on the 10s
      // client-side cache in lifecycleStageSlice.js to swallow the duplicate. On
      // a page reload with many parallel requests contending for network/CPU,
      // that gap can exceed 10s and the cache misses — confirmed via a real
      // reload showing two separate lifecycle-stages?withCounts=true calls.
      if (canViewLifecycle && !lifecycleStages.length) {
        dispatch(fetchLifecycleStagesWithCounts());
      }

      // AI agents are workspace-scoped (unlike the account-wide fetches above), so
      // this re-fires whenever the active workspace changes too, not just once.
      if (currentWorkspace?._id) {
        dispatch(fetchAiAgents({ workspaceId: currentWorkspace._id }));
      }
    }
  }, [
    dispatch,
    userDetails,
    userPermissions,
    account,
    TeamPermissions.View,
    TeamMemberPermissions.View,
    inboxSettingsTeamMembersCount,
    inboxSettingsTeamsCount,
    activeMembers?.length,
    canViewLifecycle,
    lifecycleStages.length,
    currentWorkspace?._id,
  ]);

  // Active-member presence (the green dot) doesn't need userDetails/userPermissions —
  // the endpoint only checks auth, not permissions — so it's fetched on its own, same as
  // every other fetchActiveMembers() call site (ShowMembers.jsx, AddToTeam.jsx,
  // ReportsTable.jsx). Previously this lived inside the effect above gated behind
  // `userDetails && userPermissions`; whenever that permissions round trip was slow (or
  // never resolved before the user opened Inbox), the dots stayed empty until something
  // else in that effect's deps happened to change — in practice requiring a refresh.
  //
  // Guarded on `activeMembersLoaded`, not `!activeMembers?.length` — that condition is
  // also true while the very first fetch is still in flight or was silently dropped
  // (the module-level debounce in fetchActiveMembers() rejects with no retry of its own
  // whenever another call — e.g. this same effect on a fast remount, or ShowMembers.jsx/
  // AddToTeam.jsx/ReportsTable.jsx mounted around the same time — fired within the last
  // 3s), and since a dropped/failed call never changes `activeMembers.length` (still 0
  // either way), the old length-only dependency never re-ran this effect: the dots
  // stayed empty for the rest of the session, only fixed by a full page refresh (which
  // resets the debounce map). Retrying here — once, shortly after the debounce window —
  // means a lost first attempt self-heals instead of needing that refresh.
  useEffect(() => {
    if (activeMembersLoaded) return;
    let cancelled = false;
    let retryTimer = null;
    dispatch(fetchActiveMembers())
      .unwrap()
      .catch(() => {
        if (cancelled) return;
        retryTimer = setTimeout(() => {
          if (!cancelled) dispatch(fetchActiveMembers());
        }, 3500);
      });
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [dispatch, activeMembersLoaded]);

  const toggleTeamMemberDropdown = () => setIsTeamMemberOpen((prev) => { const next = !prev; persistSectionOpen("teamMember", next); return next; });
  const toggleTeamsDropdown = () => setIsTeamsOpen((prev) => { const next = !prev; persistSectionOpen("teams", next); return next; });
  const toggleLifecycleDropdown = () => setIsLifecycleOpen((prev) => { const next = !prev; persistSectionOpen("lifecycle", next); return next; });
  const toggleAiAgentsDropdown = () => setIsAiAgentsOpen((prev) => { const next = !prev; persistSectionOpen("aiAgents", next); return next; });

  /** Same clears + URL normalize as InboxAll tab effect, run synchronously on click to avoid one frame of stale chat/URL */
  const applySidebarTabNavigation = useCallback(() => {
    dispatchInboxSidebarTabChangeClear(dispatch);
    const effectiveUserid = userid || userDetails?._id;
    navigateOffCustomerChatRoute(navigate, accountid, effectiveUserid);
  }, [dispatch, navigate, accountid, userid, userDetails?._id]);

  const handleSettingClick = (setting) => {
    if (setting !== selectedInboxTab) {
      applySidebarTabNavigation();
    }
    if (setting === "Closed" && !lazyTabsLoaded[setting]) {
      dispatch(fetchClosedTabInbox(setting));
    }
    if (setting === "Blocked Contacts" && !lazyTabsLoaded.Blocked) {
      dispatch(fetchClosedTabInbox("Blocked"));
    }
    if (setting === "Groups" && !lazyTabsLoaded.Groups) {
      dispatch(fetchClosedTabInbox("Groups"));
    }
    dispatch(setSelectedInboxTab(setting));
  };
  const items = [
    {
      tabKey: "All",
      key: "All",
      label: "All",
      icon: <DirectInbox size="100%" color={selectedInboxTab === "All" ? theme.palette.text.primary : "currentColor"} variant={selectedInboxTab === "All" ? "Bold" : "Linear"} />,
      count: inboxCounts?.All || 0,
      countLabel: formatBadgeCount(inboxCounts?.All || 0),
      selected: selectedInboxTab === "All",  // Use selectedInboxTab instead of selectedSettings
      onClick: () => handleSettingClick("All"),
    },
    {
      // Bug fix (2026-09-01): the click handler used to dispatch item.label
      // directly, which silently broke this one entry — display label "Me"
      // (Rahim, 2026-08-27: "change the you text to me") vs internal key
      // "You" (still used everywhere else: selectedInboxTab comparisons,
      // inboxCounts.You, InboxAll.jsx's contactsToShow branch). Every other
      // tab's label happens to equal its key, which is exactly why this was
      // invisible except here. tabKey is now explicit and is what onClick
      // actually dispatches — label is display-only, never read for logic.
      tabKey: "You",
      key: "You",
      label: "Me",
      icon: <ProfileCircle size="100%" color={selectedInboxTab === "You" ? theme.palette.text.primary : "currentColor"} variant={selectedInboxTab === "You" ? "Bold" : "Linear"} />,
      count: inboxCounts?.You || 0,
      countLabel: formatBadgeCount(inboxCounts?.You || 0),
      selected: selectedInboxTab === "You",  // Use selectedInboxTab instead of selectedSettings
      onClick: () => handleSettingClick("You"),
    },
    {
      tabKey: "Active",
      key: "Active",
      label: "Active",
      icon: <Activity size="100%" color={selectedInboxTab === "Active" ? theme.palette.text.primary : "currentColor"} variant={selectedInboxTab === "Active" ? "Bold" : "Linear"} />,
      count: inboxCounts?.Active || 0,
      countLabel: formatBadgeCount(inboxCounts?.Active || 0),
      selected: selectedInboxTab === "Active",  // Use selectedInboxTab instead of selectedSettings
      onClick: () => handleSettingClick("Active"),
    },
    {
      tabKey: "Unassigned",
      key: "Unassigned",
      label: "Unassigned",
      icon: <ProfileRemove size="100%" color={selectedInboxTab === "Unassigned" ? theme.palette.text.primary : "currentColor"} variant={selectedInboxTab === "Unassigned" ? "Bold" : "Linear"} />,
      count: inboxCounts?.Unassigned || 0,
      countLabel: formatBadgeCount(inboxCounts?.Unassigned || 0),
      selected: selectedInboxTab === "Unassigned",  // Use selectedInboxTab instead of selectedSettings
      onClick: () => handleSettingClick("Unassigned"),
    },
    {
      tabKey: "Closed",
      key: "Closed",
      label: "Closed",
      icon: <TickCircle size="100%" color={selectedInboxTab === "Closed" ? theme.palette.text.primary : "currentColor"} variant={selectedInboxTab === "Closed" ? "Bold" : "Linear"} />,
      count: inboxCounts?.Closed || 0,
      countLabel: formatBadgeCount(inboxCounts?.Closed || 0),
      selected: selectedInboxTab === "Closed",  // Use selectedInboxTab instead of selectedSettings
      onClick: () => handleSettingClick("Closed"),
    },
    {
      tabKey: "Blocked Contacts",
      key: "Blocked Contacts",
      label: "Blocked Contacts",
      icon: <Forbidden size="100%" color={selectedInboxTab === "Blocked Contacts" ? theme.palette.text.primary : "currentColor"} variant={selectedInboxTab === "Blocked Contacts" ? "Bold" : "Linear"} />,
      count: inboxCounts?.Blocked || 0,
      countLabel: formatBadgeCount(inboxCounts?.Blocked || 0),
      selected: selectedInboxTab === "Blocked Contacts",
      onClick: () => handleSettingClick("Blocked Contacts"),
    },
    {
      tabKey: "Groups",
      key: "Groups",
      label: "Groups",
      icon: <People size="100%" color={selectedInboxTab === "Groups" ? theme.palette.text.primary : "currentColor"} variant={selectedInboxTab === "Groups" ? "Bold" : "Linear"} />,
      count: inboxCounts?.Groups || 0,
      countLabel: formatBadgeCount(inboxCounts?.Groups || 0),
      selected: selectedInboxTab === "Groups",
      onClick: () => handleSettingClick("Groups"),
    },
    {
      tabKey: "Chat with Members",
      key: "Chat with Members",
      label: "Chat with Members",
      icon: <Profile2User size="100%" color={selectedInboxTab === "Chat with Members" ? theme.palette.text.primary : "currentColor"} variant={selectedInboxTab === "Chat with Members" ? "Bold" : "Linear"} />,
      count: teamMemberChatsUnreadThreadCount,
      countLabel: formatBadgeCount(teamMemberChatsUnreadThreadCount),
      selected: selectedInboxTab === "Chat with Members",
      onClick: () => handleSettingClick("Chat with Members"),
    },
  ].filter((item) => item.tabKey !== "Groups" || showGroupsTab);

  // Someone sitting on the Groups tab when the permission is taken away (live role change) or who has it saved as their
  // last tab goes back to All instead of an empty/blocked tab. Only once permissions are known, so a refresh on the
  // Groups tab is not bounced before the role has loaded.
  useEffect(() => {
    if (permissionsLoaded && currentWorkspaceForGroups && !showGroupsTab && selectedInboxTab === "Groups") {
      dispatch(setSelectedInboxTab("All")); // permission removed, or switched to a workspace without a QR number
    }
  }, [permissionsLoaded, currentWorkspaceForGroups, showGroupsTab, selectedInboxTab, dispatch]);


  const drawerContent = (
    <Box  sx={(theme) => ({
      color: theme.palette.text.primary,
      width: "100%",
      height: "100%", // Always 100% of parent
      overflow: "hidden", // Prevent outer container from scrolling
      paddingRight: 0,
      display: "flex",
      flexDirection: "column",
    })}>
      {/* Panel heading — this is the true leftmost panel, so it's the one that
          replaces the global TopNav's space now that it's hidden on Inbox routes
          (Home.jsx). Height matches --top-nav-height's old values exactly so
          removing the global bar doesn't shift anything. Sits outside the
          scrollable Box below (flexShrink:0 in the outer flex column) so it
          stays pinned while the list scrolls. Shared drawerContent means this
          appears both in the permanent desktop panel and the mobile drawer —
          the existing mobile close-button row further down is untouched.
          Visual only for now — the right-side toggle icon isn't wired yet. */}
      <Box
        sx={{
          flexShrink: 0,
          // TopNav's Toolbar isn't just its minHeight (40/48/50) — it also has
          // 8px/4px (or 4px/4px on xs) top/bottom padding baked in, which adds
          // to the total rendered height since this app has no global
          // box-sizing:border-box reset. Live-measured against the real TopNav
          // (getBoundingClientRect) to confirm: minHeight + vertical padding +
          // 1px border = 49/61/63/63/63, not the bare 40/48/50/50/50 minHeight.
          height: { xs: "49px", sm: "61px", md: "63px", lg: "63px", xl: "63px" },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          // Left inset matches where the filter items' ICONS actually start below
          // (mx:1 = 8px margin + padding-left:10px on each ListItemButton = 18px
          // from the panel edge, not the 8px this used to have — "Inbox" was
          // sitting visibly left of "All"/"You"'s icons, found 2026-08-18).
          pl: "18px",
          pr: "8px",
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography sx={{ fontSize: "1.3rem", fontWeight: 600, color: "text.primary" }}>
          Inbox
        </Typography>
        <IconButtonWithTooltip
          label="Collapse panel"
          size="small"
          // Wired 2026-08-18 (was visual-only) — reuses the same toggleInboxSettings
          // action/showInboxSettings state this file already reads for the mobile
          // Drawer's own open/close (see the Drawer further down), so collapsing this
          // panel here is the exact same state flip as closing the drawer there.
          // Inbox.jsx's layout math grows ChatBox to fill the freed column when this
          // goes false (lg/xl persistent-panel path).
          onClick={() => dispatch(toggleInboxSettings())}
          sx={{
            color: (theme) => theme.palette.text.secondary,
            // Was a hardcoded "#ffffff" — invisible against a light background in light
            // mode. Theme-aware now, matches in both modes.
            "&:hover": { color: (theme) => theme.palette.text.primary },
          }}
        >
          <SidebarToggleIconIx
            size="22"
            color="currentColor"
            // Always Linear (outline) — Rahim's explicit call, 2026-08-18: never fill
            // solid regardless of open/closed state (was Bold when open).
            variant="Linear"
          />
        </IconButtonWithTooltip>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          minHeight: 0, // Important for flex child
          // Shared formula (utils/scrollbarStyles.js) — single source of
          // truth, matches Dashboard.jsx exactly.
          ...slimScrollbarSx,
        }}
      >
      <List sx={{ width: "100%", padding: 0 }}>
        {/* Close button row (mobile-only overlay affordance). Workspace name moved to TopNav's right section. */}
        {(screenSize === "xs" || screenSize === "sm" || screenSize === "md") && onClose && (
          <>
            <ListItem
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                padding: { xs: "4px 6px 0 6px", sm: "6px 8px 0 8px", md: "6px 10px 0 10px" },
                paddingBottom: 0,
                paddingTop: { xs: "4px", sm: "6px", md: "6px" },
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                minHeight: { xs: "32px", sm: "36px", md: "32px" },
              }}
            >
              <IconButton
                onClick={onClose}
                sx={{
                  color: (theme) => theme.palette.text.primary,
                  padding: { xs: "2px", sm: "4px", md: "6px" },
                  "& .MuiSvgIcon-root": {
                    fontSize: { xs: "0.875rem", sm: "1rem", md: "1.125rem" },
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </ListItem>
            <Divider sx={{ color: (theme) => theme.palette.divider }} />
          </>
        )}

        {/* Multi-number: self-hides unless this QR workspace has more than one number. */}
        <InboxNumberFilter />

        {isCoreLoading ? (
          // This ternary only stands in for the filter-items list below (items.map) —
          // Lifecycle/Team Member/Teams render unconditionally right after it, each with
          // their own independent loading state (lifecycleLoading/teamDataLoading). This
          // used to also fake Team Member + Teams section skeletons here, which duplicated
          // those real sections and made this block far taller than the 7 items it actually
          // replaces — Lifecycle (and everything below it) visibly sat near the bottom of
          // the panel while this was loading, then snapped up once it resolved. Fixed by
          // only rendering what this ternary actually hides: the 7 nav-item rows.
          <Box sx={{ py: 0.5 }}>
            {[...Array(7)].map((_, i) => (
              <SidebarRowSkeleton key={i} />
            ))}
          </Box>
        ) : (
          // Extracted into SidebarNavList.jsx (2026-09-10) so other rails wanting
          // this exact look share one implementation instead of hand-copying it —
          // Settings' new sidebar is the first other consumer. Zero behavior change.
          <SidebarNavList items={items} />
        )}

        <Divider sx={{ color: "main.secondary" }} />

        {/* AI Agents section — same collapsible-rail shape as Lifecycle/Team Member/Teams
            below, one row per workspace AI agent (aiAgentsSlice), each row filtering the
            Inbox to the contacts that agent currently owns via AI Takeover (see
            eventHandlers/aiTakeoverEventHandler.js and inboxController.js's 'Agent'
            onlyCategory branch). Count badge only appears once a row has been clicked
            at least once this session (agentByAgentId is fetched on demand, unlike
            Team Member/Teams' counts which come pre-computed with every inbox fetch).
            Placed above Lifecycle (Rahim's ask, 2026-09-01). */}
        <ListItemButton
          onClick={toggleAiAgentsDropdown}
          disableRipple sx={{ py: "8px", pl: "18px", pr: "10px", "&:hover": { backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)") } }}
        >
          <ListItemText
            primary="AI Agents"
            primaryTypographyProps={{
              sx: { color: (theme) => theme.palette.text.primary, fontSize: "0.9375rem", fontWeight: 600 },
            }}
          />
          {aiAgents.length === 0 && (
            <IconButtonWithTooltip
              label="Add AI agent"
              size="small"
              onClick={goToAiSettings}
              sx={{ width: 26, height: 26, mr: 0.5, color: (theme) => theme.palette.text.secondary, "&:hover": { color: (theme) => theme.palette.text.primary } }}
            >
              <Add size="20" color="currentColor" />
            </IconButtonWithTooltip>
          )}
          <Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", color: (theme) => theme.palette.text.primary }}>
            {isAiAgentsOpen ? <ArrowUp2 size="100%" color="currentColor" /> : <ArrowDown2 size="100%" color="currentColor" />}
          </Box>
        </ListItemButton>
        <Collapse in={isAiAgentsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pt: 0.5, pb: 1 }}>
            {aiAgentsLoading && aiAgents.length === 0 ? (
              <Box sx={{ py: 0.5 }}>
                {[...Array(2)].map((_, i) => (
                  <SidebarRowSkeleton key={i} />
                ))}
              </Box>
            ) : aiAgents.length === 0 ? (
              <ListItem>
                <Typography
                  sx={{
                    color: (theme) => theme.palette.text.secondary,
                    fontSize: { xs: "0.75rem", sm: "0.8125rem", md: "0.8125rem", lg: "0.875rem", xl: "0.875rem" },
                    textAlign: "center",
                    width: "100%",
                    py: 1,
                  }}
                >
                  No AI agents configured yet
                </Typography>
              </ListItem>
            ) : (
              aiAgents.map((agent, index) => {
                const tabValue = `agent:${agent._id}`;
                const isSelected = selectedInboxTab === tabValue;
                // agentByAgentId (real fetch once the row's been clicked this session)
                // over aiAgentCounts (upfront snapshot + live deltas, survives refresh)
                // — same "prefer the freshest known source" the rest of this rail uses.
                const count = agentByAgentId[agent._id]?.count ?? aiAgentCounts[agent._id] ?? 0;
                return (
                  <ListItemButton
                    key={agent._id}
                    selected={isSelected}
                    onClick={() => {
                      if (tabValue !== selectedInboxTab) {
                        applySidebarTabNavigation();
                      }
                      dispatch(setSelectedInboxTab(tabValue));
                      // Always dispatch — fetchAgentTabInbox itself checks the per-agent
                      // session cache (agentByAgentId) and skips the network call if this
                      // agent was already visited this session (same pattern as the
                      // Lifecycle rail's fetchLifecycleTabInbox above).
                      dispatch(fetchAgentTabInbox({ agentId: agent._id }));
                    }}
                    sx={{
                      cursor: "pointer",
                      borderRadius: "8px",
                      transition: "background-color 0.12s ease",
                      padding: "8px 10px",
                      mx: 1,
                      mt: index === 0 ? 0 : 0.5,
                      width: "auto",
                      "&.Mui-selected": {
                        backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"),
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.09)"),
                      },
                      "&:hover": {
                        backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)"),
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: (theme) => (isSelected ? activeRowColor : inactiveRowColor),
                        }}
                      >
                        <Magicpen size="100%" color="currentColor" variant={isSelected ? "Bold" : "Linear"} />
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={agent.name || "AI Agent"}
                      sx={{ minWidth: 0 }}
                      primaryTypographyProps={{
                        sx: {
                          color: (theme) => (isSelected ? activeRowColor : inactiveRowColor),
                          fontSize: "0.9375rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        },
                      }}
                    />
                    {count > 0 && (
                      <Box sx={getCountBadgeSx(formatBadgeCount(count))}>
                        {formatBadgeCount(count)}
                      </Box>
                    )}
                  </ListItemButton>
                );
              })
            )}
          </List>
        </Collapse>
        <Divider sx={{ color: "main.secondary" }} />

        {/* Lifecycle section — display-only for now (stage list + real counts).
            Click-to-filter + opening a lead's chat is a deliberate follow-up pass,
            not built here yet — see leads-lifecycle-crm memory. */}
        <ListItemButton
          onClick={toggleLifecycleDropdown}
          // pl:"18px" matches the item rows' effective inset below (padding:"8px
          // 10px" + mx:1 = 18px) via PADDING, not margin — mx:1 (first attempt)
          // shrank this row's own box, so its hover fill stopped short of the
          // panel edges instead of spanning full width (Rahim, 2026-08-19).
          disableRipple sx={{ py: "8px", pl: "18px", pr: "10px", "&:hover": { backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)") } }}
        >
          <ListItemText
            primary="Lifecycle"
            primaryTypographyProps={{
              sx: { color: (theme) => theme.palette.text.primary, fontSize: "0.9375rem", fontWeight: 600 },
            }}
          />
          <Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", color: (theme) => theme.palette.text.primary }}>
            {isLifecycleOpen ? <ArrowUp2 size="100%" color="currentColor" /> : <ArrowDown2 size="100%" color="currentColor" />}
          </Box>
        </ListItemButton>
        <Collapse in={isLifecycleOpen} timeout="auto" unmountOnExit>
          {/* pt trimmed from 1 (8px) to 0.5 (4px) — combined with the header's
              own bottom padding this was a 16px gap before the first row,
              "way too much" (Rahim, 2026-08-19). */}
          <List component="div" disablePadding sx={{ pt: 0.5, pb: 1 }}>
            {!canViewLifecycle ? (
              <ListItem>
                <Typography
                  sx={{
                    color: (theme) => theme.palette.common.black,
                    fontStyle: "italic",
                    padding: { xs: "3px 6px", sm: "4px 8px", md: "5px 10px", lg: "6px 12px", xl: "7px 14px" },
                    fontSize: { xs: "0.625rem", sm: "0.6875rem", md: "0.6875rem", lg: "0.75rem", xl: "0.8125rem" },
                    textAlign: "center",
                    width: "100%",
                    backgroundColor: (theme) => theme.palette.grey[300],
                    borderRadius: "4px",
                    margin: { xs: "4px", sm: "6px", md: "5px", lg: "6px", xl: "8px" },
                  }}
                >
                  You don't have permission to view Lifecycle
                </Typography>
              </ListItem>
            ) : lifecycleLoading && lifecycleStages.length === 0 ? (
              <Box sx={{ py: 0.5 }}>
                {[...Array(3)].map((_, i) => (
                  <SidebarRowSkeleton key={i} />
                ))}
              </Box>
            ) : lifecycleStages.length === 0 ? (
              <ListItem>
                <Typography
                  sx={{
                    color: (theme) => theme.palette.common.black,
                    fontStyle: "italic",
                    padding: { xs: "3px 6px", sm: "4px 8px", md: "5px 10px", lg: "6px 12px", xl: "7px 14px" },
                    fontSize: { xs: "0.625rem", sm: "0.6875rem", md: "0.6875rem", lg: "0.75rem", xl: "0.8125rem" },
                    textAlign: "center",
                    width: "100%",
                    backgroundColor: (theme) => theme.palette.grey[300],
                    borderRadius: "4px",
                    margin: { xs: "4px", sm: "6px", md: "5px", lg: "6px", xl: "8px" },
                  }}
                >
                  No lifecycle stages configured yet
                </Typography>
              </ListItem>
            ) : (
              lifecycleStages.map((stage, index) => (
                <ListItemButton
                  key={stage._id}
                  selected={selectedInboxTab === `lifecycle:${stage._id}`}
                  onClick={() => {
                    const tabValue = `lifecycle:${stage._id}`;
                    if (tabValue !== selectedInboxTab) {
                      applySidebarTabNavigation();
                    }
                    dispatch(setSelectedInboxTab(tabValue));
                    // Always dispatch — fetchLifecycleTabInbox itself checks the
                    // per-stage session cache (lifecycleByStage) and skips the network
                    // call if this stage was already visited this session (same
                    // "fetch once, cache" guarantee as Closed/Blocked), kept accurate in
                    // the background by socketHandler.js even while a different stage
                    // is the one on screen. Centralizing the check inside the thunk
                    // means this click handler doesn't need to duplicate that logic.
                    dispatch(fetchLifecycleTabInbox({ stageId: stage._id }));
                  }}
                  disableRipple
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    padding: "8px 10px",
                    mx: 1,
                    // Gap between adjacent rows — otherwise two consecutive selected/hover
                    // pills touch with zero gap and read as one fused shape.
                    mt: index === 0 ? 0 : 0.5,
                    borderRadius: "8px",
                    // Same fix as the top filter list above (MUI ListItemButton's own
                    // hardcoded `&.Mui-selected` green tint otherwise wins over a plain
                    // backgroundColor at higher specificity — see that list's own comment).
                    // This list never had the override, hence the green instead of grey.
                    "&.Mui-selected": {
                      backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"),
                    },
                    "&.Mui-selected:hover": {
                      backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.09)"),
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Box sx={{ width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {/* stage.icon is a plain emoji character (emoji-mart picker in
                          LifecycleSettings.jsx), not a component — same convention
                          already used by the Dashboard's LifecycleRow.jsx. */}
                      {stage.icon ? (
                        <Box sx={{ fontSize: "1.125rem", lineHeight: 1 }}>{stage.icon}</Box>
                      ) : (
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            backgroundColor: stage.color || "#64748b",
                          }}
                        />
                      )}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={stage.name}
                    primaryTypographyProps={{
                      sx: {
                        color: (theme) => theme.palette.text.primary,
                        fontSize: "0.9375rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  />
                  {lifecycleCounts[stage._id] > 0 && (
                    <Box sx={getCountBadgeSx(formatBadgeCount(lifecycleCounts[stage._id]))}>
                      {formatBadgeCount(lifecycleCounts[stage._id])}
                    </Box>
                  )}
                </ListItemButton>
              ))
            )}
          </List>
        </Collapse>
        <Divider sx={{ color: "main.secondary" }} />

        <ListItemButton
          onClick={toggleTeamMemberDropdown}
          // pl:"18px" matches the item rows' effective inset below (padding:"8px
          // 10px" + mx:1 = 18px) via PADDING, not margin — mx:1 (first attempt)
          // shrank this row's own box, so its hover fill stopped short of the
          // panel edges instead of spanning full width (Rahim, 2026-08-19).
          disableRipple sx={{ py: "8px", pl: "18px", pr: "10px", "&:hover": { backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)") } }}
        >
          <ListItemText
            primary="Team Member"
            primaryTypographyProps={{
              sx: { color: (theme) => theme.palette.text.primary, fontSize: "0.9375rem", fontWeight: 600 },
            }}
          />
          {teamMembers.length === 0 && (
            <IconButtonWithTooltip
              label="Add team member"
              size="small"
              onClick={goToSettingsOption("Team Members")}
              sx={{ width: 26, height: 26, mr: 0.5, color: (theme) => theme.palette.text.secondary, "&:hover": { color: (theme) => theme.palette.text.primary } }}
            >
              <Add size="20" color="currentColor" />
            </IconButtonWithTooltip>
          )}
          <Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", color: (theme) => theme.palette.text.primary }}>
            {isTeamMemberOpen ? <ArrowUp2 size="100%" color="currentColor" /> : <ArrowDown2 size="100%" color="currentColor" />}
          </Box>
        </ListItemButton>
        <Collapse in={isTeamMemberOpen} timeout="auto" unmountOnExit>
          {/* pt trimmed from 1 (8px) to 0.5 (4px) — combined with the header's
              own bottom padding this was a 16px gap before the first row,
              "way too much" (Rahim, 2026-08-19). */}
          <List component="div" disablePadding sx={{ pt: 0.5, pb: 1 }}>
            {!TeamMemberPermissions.View ? (
              <ListItem>
                <Typography
                  sx={{
                    color: (theme) => theme.palette.common.black,
                    fontStyle: "italic",
                    padding: { xs: "3px 6px", sm: "4px 8px", md: "5px 10px", lg: "6px 12px", xl: "7px 14px" },
                    fontSize: { xs: "0.625rem", sm: "0.6875rem", md: "0.6875rem", lg: "0.75rem", xl: "0.8125rem" },
                    textAlign: "center",
                    width: "100%",
                    backgroundColor: (theme) => theme.palette.grey[300],
                    borderRadius: "4px",
                    margin: { xs: "4px", sm: "6px", md: "5px", lg: "6px", xl: "8px" },
                  }}
                >
                  You don't have permission to view team members
                </Typography>
              </ListItem>
            ) : teamDataLoading ? (
              <Box sx={{ py: 0.5 }}>
                {[...Array(3)].map((_, i) => (
                  <SidebarRowSkeleton key={i} />
                ))}
              </Box>
            ) : teamMembers.length === 0 ? (
              <ListItem>
                <Typography
                  sx={{
                    color: (theme) => theme.palette.text.secondary,
                    fontSize: { xs: "0.75rem", sm: "0.8125rem", md: "0.8125rem", lg: "0.875rem", xl: "0.875rem" },
                    textAlign: "center",
                    width: "100%",
                    py: 1,
                  }}
                >
                  No members added yet
                </Typography>
              </ListItem>
            ) : (
              teamMembers.map((member, index) => {
                const isSelf = member._id === userDetails?._id;
                const isActive = isSelf
                  ? isSocketConnected
                  : activeMembers?.some(
                      (activeMember) => activeMember._id === member._id
                    );
                return (
                  <ListItemButton
                    key={index}
                    selected={selectedInboxTab === member._id}
                    onClick={() => {
                      if (member._id !== selectedInboxTab) {
                        applySidebarTabNavigation();
                      }
                      dispatch(setSelectedInboxTab(member._id));
                      dispatch(setSelectedTeamMember(member));
                      dispatch(setSelectedTeam(null));
                      dispatch(setSelectedSettings("TeamMember"));
                    }}
                    sx={{
                      cursor: "pointer",
                      borderRadius: "8px",
                      transition: "background-color 0.12s ease",
                      padding: "8px 10px",
                      mx: 1,
                      // Gap between adjacent rows — otherwise two consecutive selected/hover
                      // pills touch with zero gap and read as one fused shape.
                      mt: index === 0 ? 0 : 0.5,
                      width: "auto",
                      // Same fix as the first section's items — ListItemButton hardcodes its
                      // own `&.Mui-selected` green background at higher specificity than a
                      // flat sx backgroundColor, so the override has to target that exact
                      // selector or it's silently ignored.
                      "&.Mui-selected": {
                        backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"),
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.09)"),
                      },
                      "&:hover": {
                        backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)"),
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Box sx={{ position: "relative", display: "inline-flex" }}>
                        {/* Grey when neither selected nor online, same rule this app already
                            uses elsewhere for unselected icons (e.g. ChatInfoRail.jsx's rail
                            icons) — was flat text.primary always, regardless of state (Rahim,
                            2026-08-27: "when an icon is not selected... it becomes grey... if
                            a team member is online then its alright to keep it black"). isSelf
                            counts as online here too, matching the green-dot logic just below. */}
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: (theme) =>
                              selectedInboxTab === member._id || isSelf || isActive
                                ? activeRowColor
                                : inactiveRowColor,
                          }}
                        >
                          {/* Filled (Bold) when selected, both modes — same selected-icon
                              convention as the "You"/"Chat with Members" rail icons above
                              (Rahim, 2026-08-27: "when it is selected the icon would get
                              filled... for both dark and white"). Online-but-not-selected
                              stays Linear — only the color signals that, not the fill. */}
                          <ProfileCircle
                            size="100%"
                            color="currentColor"
                            variant={selectedInboxTab === member._id ? "Bold" : "Linear"}
                          />
                        </Box>
                        {/* Show green circle at bottom-right of user icon if user is active.
                            Self row: always shown, green/orange from this tab's own live
                            connection status. Other rows: unchanged — only shown (green)
                            when the server has broadcast that member as active. */}
                        {(isSelf || isActive) && (
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: { xs: "-2px", sm: "-2px", md: "-2px", lg: "-2px", xl: "-2px" },
                              right: { xs: "-1px", sm: "-1px", md: "-1px", lg: "-1px", xl: "-1px" },
                              width: { xs: "0.4375rem", sm: "0.5rem", md: "0.5rem", lg: "0.5rem", xl: "0.5625rem" },
                              height: { xs: "0.4375rem", sm: "0.5rem", md: "0.5rem", lg: "0.5rem", xl: "0.5625rem" },
                              borderRadius: "50%",
                              backgroundColor: (theme) =>
                                isSelf
                                  ? (isSocketConnected ? theme.palette.success.main : theme.palette.warning.main)
                                  : theme.palette.success.main,
                              border: (theme) => `1px solid ${theme.palette.background.paper}`,
                              zIndex: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          />
                        )}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={member.name || member._id}
                      // minWidth:0 — without it a flex child's default min-width is its
                      // own content width, so a long name/ID (member.name falls back to
                      // the raw _id, which is long) never actually shrinks enough to
                      // truncate — it just overflows straight through the badge instead
                      // (Rahim, 2026-08-29: screenshot showing an ID row colliding with
                      // its own count badge). Same fix as LifecycleStageChip.jsx earlier
                      // this session, same root cause.
                      sx={{ minWidth: 0 }}
                      primaryTypographyProps={{
                        sx: {
                          // Same selected-or-online-else-grey rule as the icon just above
                          // (Rahim, 2026-08-27: "not just the icon the text too").
                          color: (theme) =>
                            selectedInboxTab === member._id || isSelf || isActive
                              ? activeRowColor
                              : inactiveRowColor,
                          fontSize: "0.9375rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        },
                      }}
                    />
                    {member.data && member.data.length > 0 && (
                      <Box sx={getCountBadgeSx(formatBadgeCount(member.data.length))}>
                        {formatBadgeCount(member.data.length)}
                      </Box>
                    )}
                  </ListItemButton>
                );
              })
            )}
          </List>
        </Collapse>
        <Divider sx={{ color: "main.secondary" }} />

        <ListItemButton
          onClick={toggleTeamsDropdown}
          // pl:"18px" matches the item rows' effective inset below (padding:"8px
          // 10px" + mx:1 = 18px) via PADDING, not margin — mx:1 (first attempt)
          // shrank this row's own box, so its hover fill stopped short of the
          // panel edges instead of spanning full width (Rahim, 2026-08-19).
          disableRipple sx={{ py: "8px", pl: "18px", pr: "10px", "&:hover": { backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)") } }}
        >
          <ListItemText
            primary="Teams"
            primaryTypographyProps={{
              sx: { color: (theme) => theme.palette.text.primary, fontSize: "0.9375rem", fontWeight: 600 },
            }}
          />
          {teams.length === 0 && (
            <IconButtonWithTooltip
              label="Add team"
              size="small"
              onClick={goToSettingsOption("Teams")}
              sx={{ width: 26, height: 26, mr: 0.5, color: (theme) => theme.palette.text.secondary, "&:hover": { color: (theme) => theme.palette.text.primary } }}
            >
              <Add size="20" color="currentColor" />
            </IconButtonWithTooltip>
          )}
          <Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", color: (theme) => theme.palette.text.primary }}>
            {isTeamsOpen ? <ArrowUp2 size="100%" color="currentColor" /> : <ArrowDown2 size="100%" color="currentColor" />}
          </Box>
        </ListItemButton>
        <Collapse in={isTeamsOpen} timeout="auto" unmountOnExit>
          {/* pt trimmed from 1 (8px) to 0.5 (4px) — combined with the header's
              own bottom padding this was a 16px gap before the first row,
              "way too much" (Rahim, 2026-08-19). */}
          <List component="div" disablePadding sx={{ pt: 0.5, pb: 1 }}>
            {!TeamPermissions.View ? (
              <ListItem>
                <Typography
                  sx={{
                    color: (theme) => theme.palette.common.black,
                    fontStyle: "italic",
                    padding: { xs: "3px 6px", sm: "4px 8px", md: "5px 10px", lg: "6px 12px", xl: "7px 14px" },
                    fontSize: { xs: "0.625rem", sm: "0.6875rem", md: "0.6875rem", lg: "0.75rem", xl: "0.8125rem" },
                    textAlign: "center",
                    width: "100%",
                    backgroundColor: (theme) => theme.palette.grey[300],
                    borderRadius: "4px",
                    margin: { xs: "4px", sm: "6px", md: "5px", lg: "6px", xl: "8px" },
                  }}
                >
                  You don't have permission to view teams
                </Typography>
              </ListItem>
            ) : teamDataLoading ? (
              <Box sx={{ py: 0.5 }}>
                {[...Array(2)].map((_, i) => (
                  <SidebarRowSkeleton key={i} />
                ))}
              </Box>
            ) : teams.length === 0 ? (
              <ListItem>
                <Typography
                  sx={{
                    color: (theme) => theme.palette.text.secondary,
                    fontSize: { xs: "0.75rem", sm: "0.8125rem", md: "0.8125rem", lg: "0.875rem", xl: "0.875rem" },
                    textAlign: "center",
                    width: "100%",
                    py: 1,
                  }}
                >
                  No teams created
                </Typography>
              </ListItem>
            ) : (
              teams.map((team, index) => (
                <ListItemButton
                  key={index}
                  selected={selectedInboxTab === team._id}
                  onClick={() => {
                    if (team._id !== selectedInboxTab) {
                      applySidebarTabNavigation();
                    }
                    dispatch(setSelectedInboxTab(team._id));
                    dispatch(setSelectedTeam(team));
                    dispatch(setSelectedTeamMember(null));
                    dispatch(setSelectedSettings("Team"));
                  }}
                  sx={{
                    cursor: "pointer",
                    borderRadius: "8px",
                    transition: "background-color 0.12s ease",
                    padding: "8px 10px",
                    mx: 1,
                    // Gap between adjacent rows — otherwise two consecutive selected/hover
                    // pills touch with zero gap and read as one fused shape.
                    mt: index === 0 ? 0 : 0.5,
                    width: "auto",
                    "&.Mui-selected": {
                      backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"),
                    },
                    "&.Mui-selected:hover": {
                      backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.09)"),
                    },
                    "&:hover": {
                      backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)"),
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {/* Same selected-else-grey rule as the Team Member rows above (Rahim,
                        2026-08-27: "same for the sales one") — no online concept for a team,
                        so selection is the only condition here. */}
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: (theme) =>
                          selectedInboxTab === team._id ? activeRowColor : inactiveRowColor,
                      }}
                    >
                      <Profile2User
                        size="100%"
                        color="currentColor"
                        variant={selectedInboxTab === team._id ? "Bold" : "Linear"}
                      />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={team.name || team.teamName}
                    // Same overflow fix as the Team Member rows above — a long team name
                    // needs minWidth:0 to actually shrink instead of overflowing past the
                    // count badge (Rahim, 2026-08-29: "check the same for the teams if
                    // its a long name it should be ... properly").
                    sx={{ minWidth: 0 }}
                    primaryTypographyProps={{
                      sx: {
                        // Same selected-else-grey rule as the icon just above.
                        color: (theme) =>
                          selectedInboxTab === team._id ? activeRowColor : inactiveRowColor,
                        fontSize: "0.9375rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  />
                  {team.data && team.data.length > 0 && (
                    <Box sx={getCountBadgeSx(formatBadgeCount(team.data.length))}>
                      {formatBadgeCount(team.data.length)}
                    </Box>
                  )}
                </ListItemButton>
              ))
            )}
          </List>
        </Collapse>
      </List>
      </Box>
    </Box>
  );

  // If inside a drawer, just render the content directly
  if (isInsideDrawer) {
    return (
      <Box
        sx={{
          height: "100%",
          backgroundColor: (theme) => theme.palette.background.default,
          display: "flex",
          width: "100%",
          overflowY: "auto",
          // Shared formula (utils/scrollbarStyles.js) — single source of
          // truth, matches Dashboard.jsx exactly.
          ...slimScrollbarSx,
        }}
      >
        {drawerContent}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: { xs: "100%", sm: "100%", md: "100%", lg: "100vh", xl: "100vh" },
        backgroundColor: (theme) => theme.palette.background.default,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        // Was overflowY:"auto" here too — made this OUTER wrapper its own scroll
        // container, so the whole panel (the "Inbox" header included) scrolled as
        // one block, defeating drawerContent's own internal pinned-header/
        // scrollable-list structure below (Rahim, 2026-08-19: header "gets
        // scrolled as well"). This wrapper doesn't need to scroll itself —
        // drawerContent already handles that internally.
        overflow: "hidden",
      }}
    >
      {(isXlScreen || screenSize === "lg") ? (
        // flex:1 + minHeight:0 (not flexShrink:0) — this wrapper must actually
        // receive the real bounded height from its now-overflow:hidden parent
        // above, or drawerContent's own "height:100%" has nothing real to resolve
        // against and its internal list never gets a scrollable bound either
        // (broke scrolling entirely on the first attempt — Rahim caught it,
        // 2026-08-19).
        <Box sx={{ display: "flex", width: "100%", flex: 1, minHeight: 0 }}>{drawerContent}</Box>
      ) : (
        <Drawer
          variant="temporary"
          open={showInboxSettings}
          onClose={() => dispatch(toggleInboxSettings())}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: "block",
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: "80%",
              backgroundColor: (theme) => theme.palette.background.default,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </Box>
  );
};

export default InboxSettings;

